import type { OrgLayerConfigDocument, OrgLayerNodeEntry } from '~/api/orgLayerConfig';

/**
 * The org-layer-config slice of store state. Membership-only: `nodeIds` is the complete,
 * order-preserving leaf-id set; `missingSince` carries the server's GC stamps verbatim
 * (the client never interprets them).
 */
export interface OrgLayerConfigState {
  nodeIds: string[];
  missingSince: Map<string, string>;
}

/** Project a hydrated org document into store state. */
export function documentToState(document: OrgLayerConfigDocument): OrgLayerConfigState {
  const nodeIds: string[] = [];
  const missingSince = new Map<string, string>();
  for (const [nodeId, entry] of Object.entries(document.nodes)) {
    nodeIds.push(nodeId);
    if (entry.missingSince !== undefined) missingSince.set(nodeId, entry.missingSince);
  }
  return { nodeIds, missingSince };
}

/**
 * Rebuild the whole org document from store state — membership-only, with the server's
 * `missingSince` echoed back untouched. Never emits `visible`/`pinned`. Inverse of
 * {@link documentToState}. This is what the sync's PUT serializes; no validation (server is authority).
 */
export function stateToDocument(state: OrgLayerConfigState): OrgLayerConfigDocument {
  // Null-prototype: node ids come from store state — on a plain `{}` a `__proto__` key would
  // set the prototype instead of an own entry and silently drop the id.
  const nodes: Record<string, OrgLayerNodeEntry> = Object.create(null);
  for (const nodeId of state.nodeIds) {
    const stamp = state.missingSince.get(nodeId);
    nodes[nodeId] = stamp !== undefined ? { missingSince: stamp } : {};
  }
  return { nodes };
}
